import { FC } from 'react';
import { GrFacebookOption, GrInstagram, GrLinkedinOption, GrYoutube } from 'react-icons/gr';
import { Accordion } from '@chakra-ui/react';
import Item from '@components/accordion/Item';
import { AnalyiticsAppDownload, Analytics, AnalyticsSearchType } from '@components/analytics/analytics';
import { spanishLabelToCountryCodeMapper } from '@components/common/country/countries';
import Image from '@components/images/RentennialsNextImage';
import RentennialsNextImage from '@components/images/RentennialsNextImage';
import { groupByClause } from '@utils/index';
import { tw } from '@utils/tailwind-utils';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { City } from 'src/api/types/cities';

import SocialNetworks from './SocialNetworks';
import { footerLinks } from './utils';

interface InfoProps {
  classNames?: string;
  containerClassName?: string;
  isTablet: boolean;
  cities: City[];
  enabledNewsLetter?: boolean;
  showAllIcons?: boolean;
}
const Info: FC<InfoProps> = ({ classNames, containerClassName, isTablet, cities, showAllIcons = false }) => {
  const { t } = useTranslation('footer');

  const objectCities = groupByClause(cities, ({ country }) => country);

  const handleFooterCityLink = (id: string, name: string) => {
    Analytics.event(AnalyticsSearchType.WEB_FOOTER_LINK, {
      city_name: name,
      city_id: id
    });
  };
  // const isNewsLetterEnabled = isFeatureEnabled(Features.newsLetter) || enabledNewsLetter;

  const handleTrackGoToAppStore = () => {
    Analytics.event(AnalyiticsAppDownload.APP_STORE, {
      date: new Date()
    });
  };

  const handleTrackGoToPlayStore = () => {
    Analytics.event(AnalyiticsAppDownload.PLAY_STORE, {
      date: new Date()
    });
  };

  return (
    <div className={tw('px-4 tablet:px-8', classNames)}>
      <div className={tw('rounded-[40px] bg-white px-8 py-10 tablet:px-16', containerClassName)}>
        <div className="flex flex-col items-center justify-center gap-y-10 pt-5 tablet:flex-row tablet:justify-between">
          <div className="inline-flex w-full items-end justify-between">
            <div className="pr-10 pt-4 tablet:flex-1">
              <RentennialsNextImage
                priority
                alt="Rentennials"
                src="/images/Logo_Rentennials_Bottom.svg"
                height={44}
                width={229}
                className="h-[44px] w-[229px]"
              />
            </div>
            <div>
              {showAllIcons && (
                <div className="hidden tablet:block">
                  <SocialNetworks />
                </div>
              )}
            </div>
          </div>
        </div>
        <hr className="mt-6" />
        <div className="flex flex-col justify-between  gap-y-4 pb-8 pt-6 tablet:flex-row tablet:pl-4 tablet:pt-16">
          {isTablet ? (
            <>
              <div className="flex-1">
                <p className="font-bree-serif text-[20px] font-normal text-rentennials-primary">
                  {t(footerLinks.aboutUs.mainText)}
                </p>
                <ul className="list-item pt-4 font-bree-serif text-[13px] font-normal text-[#545454]">
                  {footerLinks.aboutUs.links.map(({ label, url }, index) => (
                    <li key={`${label}-${index}`} className="py-1">
                      {url ? (
                        <a href={url} target="_blank" rel="noopener noreferrer">
                          {t(label)}
                        </a>
                      ) : (
                        t(label)
                      )}
                    </li>
                  ))}
                </ul>
              </div>
              {cities.length > 0 && (
                <div className="flex-1">
                  <p className="font-bree-serif text-[20px] font-normal text-rentennials-primary">
                    {t(footerLinks.locations.mainText)}
                  </p>
                  <div className="pt-4 font-bree-serif text-[10px] font-normal text-[#545454]">
                    {Object.keys(objectCities).map((cityKey) => (
                      <Accordion defaultIndex={[]} allowMultiple key={cityKey} sx={{ pr: 6 }}>
                        <Item
                          sxStyles={{
                            pb: 1,
                            border: 0
                          }}
                          buttonSx={{
                            p: 0,
                            pb: 1
                          }}
                          textClasses="tablet:text-[13px] font-bree-serif"
                          accordionSx={{
                            fontSize: '18px'
                          }}
                          description={
                            <ul key="mobile.locations" className="[&>:nth-child(n)]:py-1">
                              {objectCities[cityKey].map(({ name, id, country }) => (
                                <li key={id} className="font-bree-serif text-[12px] font-normal">
                                  <Link
                                    onClick={() => handleFooterCityLink(id, name)}
                                    href={{
                                      pathname: '/listing',
                                      query: {
                                        hideDates: true,
                                        city: id,
                                        country: spanishLabelToCountryCodeMapper[country]
                                      }
                                    }}
                                  >
                                    {name}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          }
                          title={cityKey}
                          key={cityKey}
                        />
                      </Accordion>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex-1">
                <p className="font-bree-serif text-[20px] font-normal text-rentennials-primary">
                  {t(footerLinks.enterprise.mainText)}
                </p>
                <ul className="list-item pt-4 font-bree-serif text-[13px] font-normal text-[#545454]">
                  {footerLinks.enterprise.links.map(({ label, url }, index) => (
                    <li key={`${label}-${index}`} className="py-1">
                      {url ? (
                        <a href={url} target="_blank" rel="noopener noreferrer">
                          {t(label)}
                        </a>
                      ) : (
                        t(label)
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          ) : (
            <Accordion defaultIndex={[]} allowMultiple>
              {Object.entries(footerLinks).map(([category, value]) => {
                if (category === 'locations' && cities.length > 0) {
                  return (
                    <Item
                      paneSx={{
                        pr: 0
                      }}
                      description={
                        <div className="font-bree-serif text-[10px] font-normal text-[#545454]">
                          {Object.keys(objectCities).map((cityKey, index) => (
                            <Accordion defaultIndex={[]} allowMultiple key={`${cityKey} - ${index}`} sx={{ mr: 0 }}>
                              <Item
                                sxStyles={{
                                  pb: '0px',
                                  py: 1
                                }}
                                buttonSx={{
                                  p: 0,
                                  pb: 1
                                }}
                                textClasses="tablet:text-[13px] font-bree-serif"
                                description={
                                  <ul key="mobile.locations" className="[&>:nth-child(n)]:py-1">
                                    {objectCities[cityKey].map(({ name, id, country }) => (
                                      <li key={id} className="font-bree-serif text-[12px] font-normal">
                                        <Link
                                          onClick={() => handleFooterCityLink(id, name)}
                                          href={{
                                            pathname: '/listing',
                                            query: {
                                              hideDates: true,
                                              city: id,
                                              country: spanishLabelToCountryCodeMapper[country]
                                            }
                                          }}
                                        >
                                          {name}
                                        </Link>
                                      </li>
                                    ))}
                                  </ul>
                                }
                                title={cityKey}
                                key={cityKey}
                              />
                            </Accordion>
                          ))}
                        </div>
                      }
                      title={t(value.mainText)}
                      key={category}
                    />
                  );
                }
                const values = value.links.map(({ label, url }, index) => (
                  <ul key={`${label}-${index}`} className="[&:nth-child(n)]:py-1">
                    <li>
                      <a href={url} className="font-bree-serif text-[15px] font-normal">
                        {t(label)}
                      </a>
                    </li>
                  </ul>
                ));
                return <Item description={values} title={t(value.mainText)} key={category} />;
              })}
            </Accordion>
          )}
          <div className="flex flex-1 flex-col items-end gap-y-2">
            <div className="flex w-full flex-row justify-center gap-x-2 gap-y-2 tablet:w-auto tablet:flex-col tablet:justify-normal">
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://play.google.com/store/apps/details?id=com.rentennials"
                onClick={handleTrackGoToPlayStore}
              >
                <Image
                  src="/images/Google_Play_Store.svg"
                  alt="google playstore"
                  width={165}
                  height={44}
                  className="h-[44px] w-[125px] tablet:w-[165px]"
                />
              </a>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://apps.apple.com/us/app/rentennials-app/id1621858062"
                onClick={handleTrackGoToAppStore}
              >
                <Image
                  src="/images/Apple_Store.svg"
                  alt="apple store"
                  width={165}
                  height={44}
                  className="h-[44px] w-[125px] tablet:w-[165px]"
                />
              </a>
            </div>
            {!showAllIcons && (
              <div className="flex w-full justify-center gap-x-3 pt-2 tablet:w-auto tablet:justify-between">
                <a
                  href="https://www.facebook.com/Rentennials"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full bg-gray-100 p-1.5 text-rentennials-text hover:bg-rentennials-primary hover:text-white"
                >
                  <GrFacebookOption className="h-5 w-5 " />
                </a>
                <a
                  href="https://www.instagram.com/rentennials.app/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full bg-gray-100 p-1.5 text-rentennials-text hover:bg-rentennials-primary hover:text-white"
                >
                  <GrInstagram className="h-5 w-5 " />
                </a>
                <a
                  href="https://www.linkedin.com/company/rentennials-app/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full bg-gray-100 p-1.5 text-rentennials-text hover:bg-rentennials-primary hover:text-white"
                >
                  <GrLinkedinOption className="h-5 w-5 " />
                </a>
                <a
                  href="https://www.youtube.com/channel/UCo4teV3Cl9Fseb8K4aOHUQQ"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full bg-gray-100 p-1.5 text-rentennials-text hover:bg-rentennials-primary hover:text-white"
                >
                  <GrYoutube className="h-5 w-5 " />
                </a>
              </div>
            )}
          </div>
          {showAllIcons && (
            <div className="block tablet:hidden">
              <SocialNetworks />
            </div>
          )}
        </div>
        <hr />
        <div className="w-full pt-6 text-center font-roboto text-[11px]">
          <p>{t('rentennials-trade-mark')}</p>
        </div>
      </div>
    </div>
  );
};

export default Info;
